/**
 * The UNIFIED question set (docs/43 §3.4, crossmarc/steward-app#2699 slice 1): one id and one
 * validator per real question, whichever form asks it. CONTRACT ONLY: nothing outside this
 * package consumes it yet, and the live snapshot and onboarding schemas are untouched, so no
 * live behaviour moves in this slice. The one live effect: the next dev pack seed writes the
 * five `CTYPE#` rows and the `QMANIFEST#client-questions` row (inert, but live state).
 *
 * ## Derived, not copied
 *
 * The set is built at import from the two sources that exist today, and carries no third copy of
 * a rule the sources already state:
 *   - the SNAPSHOT questions come from {@link QUESTION_CATALOGUE} (which walks
 *     `IntakeQueueSubmissionSchema`), and each leaf's type, options and live cap come from zod's
 *     own JSON Schema export of that same schema;
 *   - the ONBOARDING questions come from {@link ONBOARDING_FORM_FIELDS} (kind, key) and the
 *     live caps the pack compiles from them.
 * What is hand-written, because no source states it: the unified id of an onboarding key
 * ({@link ONBOARDING_ID_OVERRIDES}), and the REALISTIC cap per question class
 * ({@link UNIFIED_CAPS}, {@link SHORT_TEXT_SEGMENT_CAPS}, {@link STRING_LIST_CAPS}). Both are
 * pinned by `unifiedQuestions.test.ts`.
 *
 * ## Ids
 *
 *   - A SNAPSHOT id is its own unified id (`aboutYou.firstName`, `cashAccounts[].institution`).
 *   - An ONBOARDING key `client_X` is `aboutYou.X`, `spouse_X` is `spousePartner.spouse.X`, and any
 *     other `section_Name` is `section.Name` (`address.legalStreet`, `about.hobbies`).
 *   - The questions BOTH forms ask today are exactly {@link ONBOARDING_PREFILL_KEYS} (19 keys:
 *     the client's and spouse's contact and work details, `spouse_included`, the children's names
 *     and birth dates, and the CPA's and attorney's name and firm), and so share one id. The
 *     onboarding children are two PARALLEL string arrays; unified they are the `name` and
 *     `dateOfBirth` fields of the `spousePartner.children` list. A test pins that this overlap
 *     equals `ONBOARDING_PREFILL_KEYS`, so a question added to both forms without being joined
 *     (or joined without being a prefill key) goes red.
 *
 * ## What is NOT a question
 *
 * The onboarding row's server-owned markers (`sentAt`, `identityProvided*`, the per-section
 * `*Saved` flags) are record state, not answers, and the two pre-split `*_employerTitle` keys
 * are read-compat only. Neither has a unified id; {@link ONBOARDING_NOT_ANSWERS} names them so
 * that every legacy id is accounted for. The SSN is not a question at all: it lives in appcore's
 * encrypted identity record and {@link checkAnswerKey} refuses the `protected-identity` class.
 *
 * ## Caps (§3.1): realistic, and NOT yet applied to any live form
 *
 * Every text question has an explicit `maxLength` (UTF-16 units, which is what the 3-bytes-per-
 * unit size test assumes), every list an item cap. They are what a FUTURE form would enforce
 * (slice 4). Where a unified cap is lower than a cap a live form enforces today,
 * {@link unifiedCapsBelowLive} lists it; `unifiedQuestions.test.ts` pins that list so each
 * tightening is a visible, deliberate decision.
 *
 * ⚠️ A text question with NO cap gets a validator that refuses every value (fail closed), and
 * `clientAnswers.test.ts`'s size test throws on it: an uncapped field cannot slip through.
 */
import { z } from 'zod';
import { IntakeQueueSubmissionSchema } from './intakeMessage.js';
import { INFO_REQUEST_ID_PATTERN } from './clientInfoRequests.js';
import { QUESTION_CATALOGUE, QuestionItemIdSchema, type QuestionClass } from './questionCatalogue.js';
import {
  ONBOARDING_ARRAY_ITEM_MAX, ONBOARDING_ARRAY_MAX_ITEMS, ONBOARDING_FORM_FIELDS, ONBOARDING_FORM_SECTIONS,
  ONBOARDING_LEGACY_EMPLOYER_TITLE_KEYS, ONBOARDING_SERVER_OWNED_KEYS, ONBOARDING_STRING_MAX,
  ONBOARDING_ARRAY_ITEM_MAX_BY_KEY, type OnboardingFieldSpec,
} from './onboardingForm.js';

export type UnifiedValueType = 'text' | 'email' | 'enum' | 'boolean' | 'multiEnum' | 'stringList' | 'list';
export type UnifiedForm = 'snapshot' | 'onboarding';
export type PlacementForm = UnifiedForm | 'both';

/** A cap a live form enforces today, per form. Derived from the sources, never hand-written. */
export interface LiveCaps {
  /** Max length of the value (or of each item, for a string list), in the form's own units. */
  maxLength?: number;
  /** Max number of rows or items. */
  maxItems?: number;
}

export interface UnifiedQuestion {
  /** The unified id. */
  id: string;
  valueType: UnifiedValueType;
  /** For an item field: the unified id of the list it belongs to. */
  listId?: string;
  /** `enum` and `multiEnum` only: the allowed values, from the snapshot schema. */
  options?: readonly string[];
  /** Unified cap on the value, in UTF-16 units: `text`, and each item of a `stringList`. */
  maxLength?: number;
  /** Unified cap on rows (`list`) or items (`stringList`, `multiEnum`). */
  maxItems?: number;
  /**
   * Which stored row holds the answer: `answers` for all but the two request lists, which have
   * their own lifecycle (a firm's request, answered later) and their own `_v`; see
   * `ClientRequestsRowSchema`. A deliberate departure from docs/43 §3.1's "one answers row",
   * which allowed a `requests` row "only if" needed: sized at today's caps it is needed.
   */
  row: 'answers' | 'requests';
  /** The union of the classes of the sources (recorded, not enforced: see `questionCatalogue.ts`). */
  classes: readonly QuestionClass[];
  /** The ids this question has in the two legacy id spaces; absent where a form does not ask it. */
  legacy: { snapshot?: string; onboarding?: string };
  /** The caps the live forms enforce today, for {@link unifiedCapsBelowLive}. */
  live: { snapshot?: LiveCaps; onboarding?: LiveCaps };
}

// ---------------------------------------------------------------------------------------------
// Hand-written inputs (pinned by the test file)
// ---------------------------------------------------------------------------------------------

/**
 * Onboarding keys whose unified id is NOT the `client_X` / `spouse_X` / `section_Name` rule: the
 * ones that join a snapshot question. (`spouse_included` and the children lists differ in
 * shape from the rule; the CPA's and attorney's sit in `currentTeam` on the snapshot.)
 */
export const ONBOARDING_ID_OVERRIDES: Readonly<Record<string, string>> = {
  spouse_included: 'spousePartner.hasSpousePartner',
  children_names: 'spousePartner.children[].name',
  children_dateOfBirths: 'spousePartner.children[].dateOfBirth',
  cpa_name: 'currentTeam.cpa.name',
  cpa_firm: 'currentTeam.cpa.firm',
  attorney_name: 'currentTeam.attorney.name',
  attorney_firm: 'currentTeam.attorney.firm',
};

/** The onboarding keys that are parallel arrays today and become item fields of a snapshot list. */
const ONBOARDING_PARALLEL_LISTS: Readonly<Record<string, string>> = {
  children_names: 'spousePartner.children',
  children_dateOfBirths: 'spousePartner.children',
};

/** Realistic caps by class, UTF-16 units (§3.1). Pinned by `unifiedQuestions.test.ts`. */
export const UNIFIED_CAPS = {
  name: 120, line: 200, phone: 40, email: 254, amount: 40, dateish: 40, city: 100, state: 60, postal: 20,
  /** Free text answered once (a scalar). */
  note: 1000,
  /** Free text inside a list row: the rows multiply it, so it is smaller. */
  itemNote: 500,
} as const;

/**
 * Row caps per list, §3.1. Ten is what makes the worst case of the whole `answers` row fit the
 * item limit with headroom (`clientAnswers.test.ts`); children stay at the live 20 because a
 * child row is small. Every list not named gets {@link DEFAULT_LIST_ROWS}. All but children are
 * BELOW the live 20, so each is reported by {@link unifiedCapsBelowLive} as a decision.
 */
export const DEFAULT_LIST_ROWS = 10;
export const LIST_ROW_CAPS: Readonly<Record<string, number>> = {
  'spousePartner.children': 20,
};

/**
 * The class of a short (live 200) text question, by the last segment of its unified id. Anything
 * longer on the snapshot (5000) or `longText` on onboarding is a `note`; anything shorter
 * (100) is an `amount`; both are decided from the source, not here. An UNLISTED segment gets no
 * cap and so fails the tests, on purpose.
 */
export const SHORT_TEXT_SEGMENT_CAPS: Readonly<Record<string, number>> = {
  firstName: UNIFIED_CAPS.name, lastName: UNIFIED_CAPS.name, middleName: UNIFIED_CAPS.name,
  name: UNIFIED_CAPS.name, institution: UNIFIED_CAPS.name, employer: UNIFIED_CAPS.name,
  title: UNIFIED_CAPS.name, firm: UNIFIED_CAPS.name, provider: UNIFIED_CAPS.name,
  owner: UNIFIED_CAPS.name, role: UNIFIED_CAPS.name, completedBy: UNIFIED_CAPS.name,
  memberName: UNIFIED_CAPS.name, member: UNIFIED_CAPS.name, relationship: UNIFIED_CAPS.name,
  almaMater: UNIFIED_CAPS.name,
  phone: UNIFIED_CAPS.phone,
  dateOfBirth: UNIFIED_CAPS.dateish, purchaseYear: UNIFIED_CAPS.dateish,
  lastValuationDate: UNIFIED_CAPS.dateish, lastReviewed: UNIFIED_CAPS.dateish,
  anniversary: UNIFIED_CAPS.dateish,
  description: UNIFIED_CAPS.line, notes: UNIFIED_CAPS.line, landWhatItIs: UNIFIED_CAPS.line,
  sportsPlayed: UNIFIED_CAPS.line, favoriteTeams: UNIFIED_CAPS.line, allergies: UNIFIED_CAPS.line,
};

/** Address segments are classed by suffix (`legalStreet`, `spouseLegalPostalCode`, ...). */
function addressSegmentCap(segment: string): number | undefined {
  if (/Street$/.test(segment)) return UNIFIED_CAPS.line;
  if (/City$/.test(segment)) return UNIFIED_CAPS.city;
  if (/State$/.test(segment)) return UNIFIED_CAPS.state;
  if (/PostalCode$/.test(segment)) return UNIFIED_CAPS.postal;
  return undefined;
}

/**
 * The two onboarding string lists that stay lists of strings (the request ids answered, and the
 * text typed for each). Item cap and list cap. The ids are request ids as minted (32 hex characters today) and are held to the live grammar `INFO_REQUEST_ID_PATTERN` (1 to 64); the texts are the one place
 * a person types at length. These two live in their OWN row ({@link UnifiedQuestion.row}): at
 * 50 items of 1000 they alone are ~150 KB, which would not leave the other answers headroom.
 */
export const STRING_LIST_CAPS: Readonly<Record<string, { maxLength: number; maxItems: number }>> = {
  'requests.answerIds': { maxLength: 64, maxItems: ONBOARDING_ARRAY_MAX_ITEMS },
  'requests.answerTexts': { maxLength: 1000, maxItems: ONBOARDING_ARRAY_MAX_ITEMS },
};

/** Every onboarding key that is NOT an answer, with the reason. Accounts for each legacy id. */
export const ONBOARDING_NOT_ANSWERS: Readonly<Record<string, 'marker' | 'legacy-employer-title'>> = Object.fromEntries([
  ...ONBOARDING_SERVER_OWNED_KEYS.map((k) => [k, 'marker'] as const),
  ...ONBOARDING_LEGACY_EMPLOYER_TITLE_KEYS.map((k) => [k, 'legacy-employer-title'] as const),
]);

// ---------------------------------------------------------------------------------------------
// Derivation
// ---------------------------------------------------------------------------------------------

type JsonLeaf = {
  type?: string; format?: string; maxLength?: number; maxItems?: number; enum?: string[];
  items?: { enum?: string[]; type?: string };
  properties?: Record<string, JsonLeaf>;
};

function snapshotLeaves(): Map<string, JsonLeaf> {
  const json = z.toJSONSchema(IntakeQueueSubmissionSchema, { io: 'input', unrepresentable: 'any' }) as JsonLeaf;
  const out = new Map<string, JsonLeaf>();
  const walk = (node: JsonLeaf, path: string, inList: boolean): void => {
    for (const [key, child] of Object.entries(node.properties ?? {})) {
      if (inList && key === 'rowId') continue;
      const id = path === '' ? key : `${path}.${key}`;
      if (child.properties) walk(child, id, inList);
      else if (child.type === 'array' && (child.items as JsonLeaf | undefined)?.properties) {
        out.set(id, child);
        walk(child.items as JsonLeaf, `${id}[]`, true);
      } else out.set(id, child);
    }
  };
  walk(json, '', false);
  return out;
}

const lastSegment = (id: string): string => id.slice(id.lastIndexOf('.') + 1);

/** The unified id of an onboarding key; `undefined` for a key that is not an answer. */
export function onboardingKeyToUnifiedId(key: string): string | undefined {
  if (Object.hasOwn(ONBOARDING_NOT_ANSWERS, key)) return undefined;
  if (Object.hasOwn(ONBOARDING_ID_OVERRIDES, key)) return ONBOARDING_ID_OVERRIDES[key];
  const idx = key.indexOf('_');
  if (idx <= 0) return undefined; // not a `section_Name` key at all
  const section = key.slice(0, idx);
  const name = key.slice(idx + 1);
  if (section === 'client') return `aboutYou.${name}`;
  if (section === 'spouse') return `spousePartner.spouse.${name}`;
  return `${section}.${name}`;
}

function onboardingLive(spec: OnboardingFieldSpec): LiveCaps | undefined {
  switch (spec.kind) {
    case 'text': return { maxLength: 200 };
    case 'longText': return { maxLength: ONBOARDING_STRING_MAX };
    case 'date': return { maxLength: ONBOARDING_STRING_MAX };
    case 'stringList':
      return { maxLength: ONBOARDING_ARRAY_ITEM_MAX_BY_KEY[spec.key] ?? ONBOARDING_ARRAY_ITEM_MAX, maxItems: ONBOARDING_ARRAY_MAX_ITEMS };
    default: return undefined;
  }
}

const FIRM_CLASSES: readonly QuestionClass[] = ['firm-visible', 'assistant-excluded'];

function capFor(id: string, snapshotLive: number | undefined, onboarding: OnboardingFieldSpec | undefined, isEmail: boolean): number | undefined {
  if (isEmail) return UNIFIED_CAPS.email;
  if ((snapshotLive ?? 0) >= 1000 || onboarding?.kind === 'longText') {
    return id.includes('[]') ? UNIFIED_CAPS.itemNote : UNIFIED_CAPS.note;
  }
  if (snapshotLive !== undefined && snapshotLive <= 100) return UNIFIED_CAPS.amount;
  const seg = lastSegment(id).replace('[]', '');
  return SHORT_TEXT_SEGMENT_CAPS[seg] ?? addressSegmentCap(seg);
}

function build(): readonly UnifiedQuestion[] {
  const leaves = snapshotLeaves();
  const byId = new Map<string, UnifiedQuestion>();
  const add = (q: UnifiedQuestion): void => {
    if (byId.has(q.id)) throw new Error(`unified id collision: ${q.id}`);
    byId.set(q.id, q);
  };
  const onboardingByUnified = new Map<string, { key: string; spec: OnboardingFieldSpec }>();
  for (const s of ONBOARDING_FORM_SECTIONS) {
    for (const spec of ONBOARDING_FORM_FIELDS[s]) {
      const id = onboardingKeyToUnifiedId(spec.key);
      if (id !== undefined) onboardingByUnified.set(id, { key: spec.key, spec });
    }
  }

  // 1. Every snapshot question, in schema order.
  for (const q of QUESTION_CATALOGUE) {
    if (q.step !== 'snapshot') continue;
    const leaf = leaves.get(q.id);
    if (!leaf) throw new Error(`no JSON schema leaf for snapshot question ${q.id}`);
    const on = onboardingByUnified.get(q.id);
    const live: UnifiedQuestion['live'] = {};
    let valueType: UnifiedValueType;
    let options: readonly string[] | undefined;
    if (q.kind === 'list') {
      valueType = 'list';
      live.snapshot = { ...(leaf.maxItems !== undefined ? { maxItems: leaf.maxItems } : {}) };
    } else if (leaf.type === 'boolean') {
      valueType = 'boolean';
    } else if (leaf.enum) {
      valueType = 'enum'; options = leaf.enum;
    } else if (leaf.type === 'array' && leaf.items?.enum) {
      valueType = 'multiEnum'; options = leaf.items.enum;
      live.snapshot = { ...(leaf.maxItems !== undefined ? { maxItems: leaf.maxItems } : {}) };
    } else if (leaf.type === 'string') {
      valueType = leaf.format === 'email' ? 'email' : 'text';
      live.snapshot = { ...(leaf.maxLength !== undefined ? { maxLength: leaf.maxLength } : {}) };
    } else {
      throw new Error(`snapshot question ${q.id} has an unmapped JSON schema leaf: ${JSON.stringify(leaf)}`);
    }
    if (on) {
      const l = onboardingLive(on.spec);
      if (l) live.onboarding = l;
    }
    const base: UnifiedQuestion = {
      id: q.id, valueType, row: 'answers' as const, ...(q.listId ? { listId: q.listId } : {}),
      ...(options ? { options } : {}),
      classes: on ? [...new Set([...FIRM_CLASSES, ...q.classes])] : q.classes,
      legacy: { snapshot: q.id, ...(on ? { onboarding: on.key } : {}) },
      live,
    };
    if (valueType === 'text' || valueType === 'email') {
      const cap = capFor(q.id, leaf.maxLength, on?.spec, valueType === 'email');
      if (cap !== undefined) base.maxLength = cap;
    } else if (valueType === 'list') {
      base.maxItems = LIST_ROW_CAPS[q.id] ?? DEFAULT_LIST_ROWS;
    } else if (valueType === 'multiEnum') {
      base.maxItems = options!.length;
    }
    add(base);
  }
  // A parallel onboarding array also bounds the list it joins (50 items live, onboarding side).
  for (const [key, listId] of Object.entries(ONBOARDING_PARALLEL_LISTS)) {
    const list = byId.get(listId);
    if (list) list.live.onboarding = { maxItems: onboardingLive(onboardingByUnified.get(onboardingKeyToUnifiedId(key)!)!.spec)!.maxItems! };
    if (list) list.legacy.onboarding ??= key;
  }

  // 2. Every onboarding-only question, in form order.
  for (const [id, { key, spec }] of onboardingByUnified) {
    if (byId.has(id)) continue;
    const live: UnifiedQuestion['live'] = {};
    const l = onboardingLive(spec);
    if (l) live.onboarding = l;
    const q: UnifiedQuestion = {
      id, valueType: 'text', row: id.startsWith('requests.') ? 'requests' : 'answers', classes: FIRM_CLASSES, legacy: { onboarding: key }, live,
    };
    if (spec.kind === 'yesNo') q.valueType = 'boolean';
    else if (spec.kind === 'stringList') {
      q.valueType = 'stringList';
      const caps = STRING_LIST_CAPS[id];
      if (caps) { q.maxLength = caps.maxLength; q.maxItems = caps.maxItems; }
    } else {
      const cap = capFor(id, undefined, spec, false);
      if (cap !== undefined) q.maxLength = cap;
      // An onboarding email is typed as text today; unified, it is the same email question.
      if (/(^|\.)email$/.test(id)) { q.valueType = 'email'; q.maxLength = UNIFIED_CAPS.email; }
    }
    add(q);
  }
  return [...byId.values()];
}

/** Every unified question: snapshot questions in schema order, then onboarding-only in form order. */
export const UNIFIED_QUESTIONS: readonly UnifiedQuestion[] = build();

const BY_ID: ReadonlyMap<string, UnifiedQuestion> = new Map(UNIFIED_QUESTIONS.map((q) => [q.id, q] as const));

export function unifiedQuestionById(id: string): UnifiedQuestion | undefined {
  return BY_ID.get(id);
}

/** Legacy snapshot id (dotted path) to unified id. Identity today; exported so callers translate through one place. */
export const SNAPSHOT_TO_UNIFIED: Readonly<Record<string, string>> = Object.fromEntries(
  UNIFIED_QUESTIONS.filter((q) => q.legacy.snapshot !== undefined).map((q) => [q.legacy.snapshot!, q.id]),
);

/** Legacy flat onboarding key to unified id. Excludes {@link ONBOARDING_NOT_ANSWERS}. */
export const ONBOARDING_TO_UNIFIED: Readonly<Record<string, string>> = Object.fromEntries(
  ONBOARDING_FORM_SECTIONS.flatMap((s) => ONBOARDING_FORM_FIELDS[s]).flatMap((spec) => {
    const id = onboardingKeyToUnifiedId(spec.key);
    return id === undefined ? [] : [[spec.key, id] as const];
  }),
);

/** The unified ids asked by BOTH forms today. */
export function unifiedOverlapIds(): string[] {
  return UNIFIED_QUESTIONS.filter((q) => q.legacy.snapshot !== undefined && q.legacy.onboarding !== undefined).map((q) => q.id);
}

/** The item fields of a list question, in schema order. */
export function unifiedItemFields(listId: string): UnifiedQuestion[] {
  return UNIFIED_QUESTIONS.filter((q) => q.listId === listId);
}

// ---------------------------------------------------------------------------------------------
// Validators: one per id
// ---------------------------------------------------------------------------------------------

/**
 * U+FFFE and U+FFFF, which XML 1.0 (and so SQS) refuses. Together with {@link LONE_SURROGATE} and
 * the control sets below, every code unit SQS refuses (U+0000-U+0008, U+000B, U+000C,
 * U+000E-U+001F, U+D800-U+DFFF, U+FFFE, U+FFFF) is refused here; `unifiedQuestions.test.ts` walks
 * all 65,536 code units to pin that. This is STRICTER than SQS, which admits C1 controls
 * (U+007F-U+009F) and carriage return: both are refused here as well.
 */
const SQS_SAFE = /^[^￾￿]*$/u;
/** A surrogate code unit with no partner: not valid text, and not transportable. Deliberately WITHOUT the `u` flag, so it reads code units. */
const LONE_SURROGATE = /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/;

/** A string bounded in UTF-16 units (zod's own `.max` counts code points since 4.6.5), trimmed, no unpaired surrogates, no U+FFFE/U+FFFF, no controls. */
/** NUL, C0 and C1 controls plus DEL: never valid in a single-line answer. */
const CONTROL_ANY = /[\u0000-\u001f\u007f-\u009f]/u;
/** The same, minus tab and newline, for a multi-line field. */
const CONTROL_EXCEPT_TAB_NL = /[\u0000-\u0008\u000b-\u001f\u007f-\u009f]/u;
/** A field is multi-line when its cap is the note cap or above (the notes and long-text boxes). */
export function isMultilineCap(max: number): boolean {
  return max >= UNIFIED_CAPS.note;
}

function boundedText(max: number): z.ZodType<string> {
  const control = isMultilineCap(max) ? CONTROL_EXCEPT_TAB_NL : CONTROL_ANY;
  // The RAW length is bounded before trimming: the row schema stores the raw string, so a trim-then-check
  // validator would let whitespace padding past the byte budget (review finding, 2026-10-31).
  return z.string().max(max).refine((s) => s.length <= max, `longer than ${max}`).regex(SQS_SAFE).refine((s) => !LONE_SURROGATE.test(s), 'unpaired surrogate').refine((s) => !control.test(s), 'control character').trim();
}

/** String lists whose every item is an info request id ({@link INFO_REQUEST_ID_PATTERN}), not free text. */
export const INFO_REQUEST_ID_ITEM_QUESTIONS: ReadonlySet<string> = new Set(['requests.answerIds']);

export function unifiedValidatorFor(q: UnifiedQuestion): z.ZodType<unknown> {
  switch (q.valueType) {
    case 'text':
      return q.maxLength === undefined ? z.never() : boundedText(q.maxLength);
    case 'email':
      // '' clears the answer (the onboarding form can erase a box); anything else must be an address.
      return q.maxLength === undefined ? z.never() : z.union([z.literal(''), z.email().max(q.maxLength)]);
    case 'boolean':
      return z.boolean();
    case 'enum':
      return z.enum(q.options as [string, ...string[]]);
    case 'multiEnum':
      return z.array(z.enum(q.options as [string, ...string[]])).max(q.maxItems!)
        .refine((a) => new Set(a).size === a.length, 'duplicate selections');
    case 'stringList':
      return q.maxLength === undefined || q.maxItems === undefined
        ? z.never()
        : z.array(INFO_REQUEST_ID_ITEM_QUESTIONS.has(q.id) ? z.string().regex(INFO_REQUEST_ID_PATTERN) : boundedText(q.maxLength)).max(q.maxItems);
    case 'list':
      // A list is a container: its rows are validated item field by item field, not as a value.
      return z.never();
  }
}

const VALIDATORS: ReadonlyMap<string, z.ZodType<unknown>> = new Map(UNIFIED_QUESTIONS.map((q) => [q.id, unifiedValidatorFor(q)] as const));

/** The one validator for a question's value. `undefined` for an unknown id. A list container's refuses every value. */
export function unifiedValidator(id: string): z.ZodType<unknown> | undefined {
  return VALIDATORS.get(id);
}

export function validateUnifiedValue(id: string, value: unknown): { ok: true; value: unknown } | { ok: false; reason: string } {
  const v = VALIDATORS.get(id);
  if (!v) return { ok: false, reason: 'unknown_question' };
  const r = v.safeParse(value);
  return r.success ? { ok: true, value: r.data } : { ok: false, reason: 'invalid_value' };
}

// ---------------------------------------------------------------------------------------------
// Caps compared with the live forms
// ---------------------------------------------------------------------------------------------

export interface CapBelowLive {
  id: string;
  what: 'maxLength' | 'maxItems';
  unified: number;
  form: UnifiedForm;
  live: number;
}

/**
 * Every place a unified cap is lower than a cap a live form enforces today, per form. Adopting
 * the unified set (slice 4) tightens each of these, so each is a decision for the founder, not
 * a side effect. (A unified cap HIGHER than a live one, e.g. an onboarding email, is not
 * listed: the live form keeps its own cap until it switches.)
 */
export function unifiedCapsBelowLive(): CapBelowLive[] {
  const out: CapBelowLive[] = [];
  for (const q of UNIFIED_QUESTIONS) {
    for (const form of ['snapshot', 'onboarding'] as const) {
      const live = q.live[form];
      if (!live) continue;
      for (const what of ['maxLength', 'maxItems'] as const) {
        const u = q[what]; const l = live[what];
        if (u !== undefined && l !== undefined && u < l) out.push({ id: q.id, what, unified: u, form, live: l });
      }
    }
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// Placement (§3.4): question id -> form, step, order. DERIVED from today's two forms.
// ---------------------------------------------------------------------------------------------

export interface FormPlacement { step: string; order: number }
export interface QuestionPlacement {
  form: PlacementForm;
  snapshot?: FormPlacement;
  onboarding?: FormPlacement;
}

function buildPlacement(): ReadonlyMap<string, QuestionPlacement> {
  const out = new Map<string, QuestionPlacement>();
  // Snapshot: step = the schema's top-level section (the wizard's step, 1:1 and in the same
  // order as `INTAKE_SECTIONS` in the SPA), order = position in the schema walk.
  let order = 0;
  for (const q of QUESTION_CATALOGUE) {
    if (q.step !== 'snapshot') continue;
    out.set(q.id, { form: 'snapshot', snapshot: { step: q.id.split(/[.[]/)[0]!, order: order++ } });
  }
  // Onboarding: step = the row section, order = position in the form's field list.
  order = 0;
  for (const s of ONBOARDING_FORM_SECTIONS) {
    for (const spec of ONBOARDING_FORM_FIELDS[s]) {
      const id = onboardingKeyToUnifiedId(spec.key)!;
      const onboarding = { step: s, order: order++ };
      const prior = out.get(id);
      out.set(id, prior ? { ...prior, form: 'both', onboarding } : { form: 'onboarding', onboarding });
    }
  }
  // A joined list container is placed wherever its item fields are.
  for (const q of UNIFIED_QUESTIONS) {
    if (q.listId === undefined) continue;
    const item = out.get(q.id)!;
    const list = out.get(q.listId)!;
    if (item.onboarding && !list.onboarding) out.set(q.listId, { ...list, form: 'both', onboarding: item.onboarding });
  }
  return out;
}

/** Question id to placement. Reproduces today's two forms exactly (pinned by the tests). */
export const QUESTION_PLACEMENT: ReadonlyMap<string, QuestionPlacement> = buildPlacement();

export function placementOf(id: string): QuestionPlacement | undefined {
  return QUESTION_PLACEMENT.get(id);
}

export function isPlacedOn(id: string, form: UnifiedForm): boolean {
  const p = QUESTION_PLACEMENT.get(id);
  return p !== undefined && (p.form === 'both' || p.form === form);
}

// ---------------------------------------------------------------------------------------------
// The writer's gate
// ---------------------------------------------------------------------------------------------

export type AnswerKeyVerdict =
  | { ok: true; question: UnifiedQuestion; itemId?: string }
  | { ok: false; reason: 'unknown' | 'protected_identity' | 'list_container' | 'item_id_required' | 'item_id_not_allowed' | 'bad_item_id' };

/**
 * The check the answers writer runs on every key before it stores or reads anything under it
 * (docs/43 §3.4): refuse a key that is unknown, or that is in the `protected-identity` class (the
 * SSN is never an answer), or that cannot hold a scalar answer (a list container). A key is a
 * question id, plus `#<itemId>` for an item field (the shape `questionAnswerKey` makes).
 *
 * `lookup` defaults to the unified set; it is a parameter so a test can pass a question carrying
 * the class, as none in the real set does today.
 */
export function checkAnswerKey(
  key: string,
  lookup: (id: string) => UnifiedQuestion | undefined = unifiedQuestionById,
): AnswerKeyVerdict {
  const hash = key.indexOf('#');
  const id = hash === -1 ? key : key.slice(0, hash);
  const itemId = hash === -1 ? undefined : key.slice(hash + 1);
  const question = lookup(id);
  if (!question) return { ok: false, reason: 'unknown' };
  if (question.classes.includes('protected-identity')) return { ok: false, reason: 'protected_identity' };
  if (question.valueType === 'list') return { ok: false, reason: 'list_container' };
  if (question.listId === undefined) {
    return itemId === undefined ? { ok: true, question } : { ok: false, reason: 'item_id_not_allowed' };
  }
  if (itemId === undefined) return { ok: false, reason: 'item_id_required' };
  if (!QuestionItemIdSchema.safeParse(itemId).success) return { ok: false, reason: 'bad_item_id' };
  return { ok: true, question, itemId };
}
